import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  CreateButton,
  TopToolbar,
  BooleanField,
  DateField,
  FunctionField,
} from 'react-admin';
import { Box, Breadcrumbs, Link, Typography, Chip } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';
import { CustomEmptyState } from '../../components/CustomEmptyState';

const BoardListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

const ConfigurationBreadcrumbs = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/configuration')}
          sx={{ cursor: 'pointer', '&:hover': { color: '#1976d2' } }}
        >
          Configuration
        </Link>
        <Typography color="text.primary">Boards</Typography>
      </Breadcrumbs>
    </Box>
  );
};

const BoardTypeField = ({ record }: { record?: any }) => {
  if (!record?.type) {
    return <span>-</span>;
  }

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'sales':
        return 'success';
      case 'projects':
        return 'primary';
      case 'leads':
        return 'warning';
      case 'custom':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Chip label={record.type} size="small" color={getTypeColor(record.type)} variant="outlined" />
  );
};

const StageCountField = ({ record }: { record?: any }) => {
  if (!record?.stages || !Array.isArray(record.stages)) {
    return <span>0</span>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <span>{record.stages.length}</span>
      <Chip
        label={`${record.stages.length} stage${record.stages.length !== 1 ? 's' : ''}`}
        size="small"
        variant="outlined"
        color="info"
      />
    </Box>
  );
};

export const BoardList = () => {
  const navigate = useNavigate();

  return (
    <List
      title="Boards"
      actions={<BoardListActions />}
      filters={[]}
      sort={{ field: 'name', order: 'ASC' }}
      empty={<CustomEmptyState resource="boards" onAction={() => navigate('/boards/create')} />}
    >
      <ConfigurationBreadcrumbs />
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="name" label="Board Name" />
        <TextField source="description" />
        <FunctionField
          source="type"
          label="Type"
          render={(record: any) => <BoardTypeField record={record} />}
        />
        <FunctionField
          source="stages"
          label="Stages"
          render={(record: any) => <StageCountField record={record} />}
        />
        <BooleanField source="isActive" label="Active" />
        <DateField source="createdAt" label="Created" showTime />
      </Datagrid>
    </List>
  );
};
